import React from "react";
import EligibilityCheckbox from "./EligibilityCheckbox";

/**
 * ChecklistItem - Unified checkbox component for Eligibility Assessment flow
 * 
 * Typography & Sizing (Spec-Kit):
 * - Label: Reduced by one step, medium weight
 * - Helper text: text-xs at 70-80% opacity
 * - Line-height: Readable, no clipping
 * 
 * Checkbox Visual:
 * - 16-18px visible box with glassmorphism
 * - ≥40x40px touch target via padding
 * - White checkmark only (no blue fill)
 * - Glass body with existing tokens
 * 
 * @param {string} id - Unique identifier for the checkbox
 * @param {string} label - Display text for the item
 * @param {string} description - Optional helper text
 * @param {boolean} checked - Whether the item is checked
 * @param {function} onCheckedChange - Callback when checked state changes
 * @param {boolean} disabled - Whether the item is disabled
 * @param {boolean} compact - Compact spacing mode
 * @param {boolean} indeterminate - Indeterminate state (dash instead of checkmark)
 * @param {boolean} error - Error state
 * @param {boolean} normalWeight - Use normal font weight instead of medium
 * @param {boolean} blurCheckedText - Mute the label once checked (strike-through + muted green;
 *   the former CSS blur made completed rows illegible)
 */
export default function ChecklistItem({
  id,
  label,
  description,
  checked = false,
  onCheckedChange,
  disabled = false,
  compact = false,
  indeterminate = false,
  error = false,
  normalWeight = false,
  className = "",
  blurCheckedText = false,
}) {
  const checkedMutedClass = blurCheckedText && checked ? "line-through text-[#557C4A]" : "";
  const labelColor = disabled
    ? "rgba(148,163,184,0.7)"
    : checkedMutedClass
      ? undefined
      : "#6B6560";

  return (
    <div
      data-testid="eligibility-checklist-item"
      className={`
        group relative flex items-center rounded-lg transition-colors duration-200
        ${compact ? 'py-1 gap-2.5' : 'py-1.5 gap-3'}
        ${disabled ? "opacity-60" : ""}
        ${error ? 'ring-1 ring-red-400/50' : ''}
        ${className}
      `}
    >
      <EligibilityCheckbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        indeterminate={indeterminate}
        className="flex-shrink-0"
      />
      
      <label
        htmlFor={id}
        className={`flex-1 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${checkedMutedClass}`}
      >
        <span
          className={`block text-[13px] leading-5 ${normalWeight ? 'font-normal' : 'font-medium'}`}
          style={{ color: labelColor }}
          data-testid="eligibility-checklist-label"
        >
          {label}
        </span>
        {description && (
          <span
            className="mt-0.5 block text-[12px] leading-5"
            style={{ color: '#6B6560' }}
            data-testid="eligibility-checklist-description"
          >
            {description}
          </span>
        )}
      </label>
    </div>
  );
}
